import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { SnapLinksComponent } from 'src/app/components/snap-links/snap-links.component';
import { DashboardViewComponent } from 'src/app/components/views/dashboard/view-dashboard.component';
import { ContractorsService } from 'src/app/services/contractors.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { PartnerService } from 'src/app/services/partner.service';
import { UsersService } from 'src/app/services/users.service';
import { Roles } from 'src/app/utility/role';
// eslint-disable-next-line @typescript-eslint/naming-convention
import Chart from 'chart.js/auto';
import { addMonths, endOfMonth, format, startOfToday } from 'date-fns';
import { Contractor } from 'src/app/models/contractor.model';
import { User } from 'src/app/models/user.model';
import { convertTimestamp } from 'src/app/utility/time';

@Component({
  selector: 'app-partner-sales-page',
  templateUrl: './sales.page.html',
  styleUrls: ['./sales.page.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    DashboardViewComponent,
    SnapLinksComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [PartnerService, CurrencyPipe],
})
export class PartnerSalesPage {
  private contractorsService = inject(ContractorsService);
  private currencyPipe = inject(CurrencyPipe);
  private navigationService = inject(NavigationService);
  private partnerService = inject(PartnerService);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);

  private contractors$ = this.contractorsService.getList().pipe(
    map((items) => items.map((it) => it.guid)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private clientsChart: Chart<'line', number[], string>;
  private licensesChart: Chart<'line', number[], string>;

  user = computed(() => this.usersService.currentUserS());

  user$ = this.usersService.getCurrentUser().pipe(
    filter((user) => user != null),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  clients$ = this.user$.pipe(
    switchMap((user) => user.role === Roles.superAdmin ? this.contractors$ : of(user.contractors ?? [])),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private licenses$ = this.user$.pipe(
    switchMap((user) => user.role === Roles.superAdmin ? combineLatest([of(user), of<string[]>([])]) : combineLatest([of(user), of<string[]>(user.contractors ?? [])])),
    switchMap(([user, contractors]) => user.role === Roles.superAdmin ?
      of(this.usersService.getList(undefined, { roles: [Roles.admin, Roles.supervisor, Roles.worker] }).pipe(
        map((users) => users.filter((it) => it.contractors?.length > 0))
      )) : contractors.map((contractor) => this.usersService.getListFromDb({ contractor, roles: [Roles.admin, Roles.supervisor, Roles.worker] }))),
    switchMap((users) => users),
    map((users) => users.filter((u) => !u.inactive)),
    startWith([]),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  partners$ = this.user$.pipe(
    map((user) => user.partners?.length ?? 0),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private income$ = this.user$.pipe(
    switchMap((user) => this.partnerService.calculateTotalIncome(user)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  snapLinks$ = combineLatest([this.clients$, this.income$, this.licenses$]).pipe(
    map(([clients, income, licenses]) => {
      return [
        {
          title: clients.length.toString(),
          name: this.translate.instant('nav.clients'),
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          action: () => { },
        },
        {
          title: licenses.length.toString(),
          name: this.translate.instant('partner.licenses'),
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          action: () => { },
        },
        {
          title: this.currencyPipe.transform(income, 'EUR'),
          name: 'MRR',
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          action: () => { },
        },
      ];
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private getClientsCount(clients: Contractor[], date: Date) {
    const end = endOfMonth(date);
    return clients.filter((it) => convertTimestamp(it.created) <= end).length;
  }

  private getUsersCount(users: User[], date: Date) {
    const end = endOfMonth(date);
    return users.filter((it) => convertTimestamp(it.creationTime) <= end).length;
  }

  clientsChart$ = this.clients$.pipe(
    switchMap((clientsGuids) => this.contractorsService.getList({ guids: clientsGuids })),
    map((clients) => {
      this.clientsChart?.destroy();
      const nativeElement = document.getElementById('clientsChart') as HTMLCanvasElement;
      const now = startOfToday();

      const dataset = [
        { label: format(addMonths(now, -3), 'MM/yy'), data: this.getClientsCount(clients, addMonths(now, -3)) },
        { label: format(addMonths(now, -2), 'MM/yy'), data: this.getClientsCount(clients, addMonths(now, -2)) },
        { label: format(addMonths(now, -1), 'MM/yy'), data: this.getClientsCount(clients, addMonths(now, -1)) },
        { label: format(now, 'MM/yy'), data: this.getClientsCount(clients, now) },
      ];
      const data = {
        labels: dataset.map((it) => it.label),
        datasets: [
          {
            label: this.translate.instant('nav.clients'),
            data: dataset.map((it) => it.data),
            borderColor: '#FF6C74',
            backgroundColor: '#FF6C74',
          },
        ],
      };
      this.clientsChart = new Chart(nativeElement, {
        type: 'line',
        data: data,
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'top',
            },
          },
          scales: {
            y: {
              ticks: {
                precision: 0,
              },
            },
          },
        },
      });
      return this.clientsChart;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  licensesChart$ = this.licenses$.pipe(
    map((users) => {
      this.licensesChart?.destroy();
      const nativeElement = document.getElementById('licensesChart') as HTMLCanvasElement;
      const now = startOfToday();

      const dataset = [
        { label: format(addMonths(now, -3), 'MM/yy'), data: this.getUsersCount(users, addMonths(now, -3)) },
        { label: format(addMonths(now, -2), 'MM/yy'), data: this.getUsersCount(users, addMonths(now, -2)) },
        { label: format(addMonths(now, -1), 'MM/yy'), data: this.getUsersCount(users, addMonths(now, -1)) },
        { label: format(now, 'MM/yy'), data: this.getUsersCount(users, now) },
      ];
      const data = {
        labels: dataset.map((it) => it.label),
        datasets: [
          {
            label: this.translate.instant('partner.licenses'),
            data: dataset.map((it) => it.data),
            borderColor: '#186617',
            backgroundColor: '#186617',
          },
        ],
      };
      this.licensesChart = new Chart(nativeElement, {
        type: 'line',
        data: data,
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'top',
            },
          },
          scales: {
            y: {
              ticks: {
                precision: 0,
              },
            },
          },
        },
      });
      return this.licensesChart;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  onLive() {
    this.navigationService.navigate(['/partner', 'live']);
  }
}
